import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import { PageErrorBoundary } from "@/components/reliability/ErrorBoundary";

// Pages
import LandingPage from "@/pages/LandingPage";
import PricingPage from "@/pages/PricingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import DashboardPage from "@/pages/DashboardPage";
import InboxPage from "@/pages/InboxPage";
import ResourcesPage from "@/pages/ResourcesPage";
import BookingsPage from "@/pages/BookingsPage";
import BookingsOverviewPage from "@/pages/BookingsOverviewPage";
import PublicBookingLinkPage from "@/pages/PublicBookingLinkPage";
import RelationsPage from "@/pages/RelationsPage";
import MembersPage from "@/pages/MembersPage";
import TeamPage from "@/pages/TeamPage";
import SettingsPage from "@/pages/SettingsPage";
import HeatingPage from "@/pages/HeatingPage";
import KeyManagementPage from "@/pages/KeyManagementPage";
import PublicBookingPage from "@/pages/PublicBookingPage";
import PublicSetlistPage from "@/pages/PublicSetlistPage";
import PublicConnectlesPage from "@/pages/PublicConnectlesPage";
import WorshipPage from "@/pages/WorshipPage";
import WorshipSetlistLibraryPage from "@/pages/WorshipSetlistLibraryPage";
import SongwritingStudioPage from "@/pages/SongwritingStudioPage";
import InventoryPage from "@/pages/InventoryPage";
import BuildingManagementPage from "@/pages/BuildingManagementPage";
import CommercePage from "@/pages/CommercePage";
import ThemeEditorPage from "@/pages/commerce/ThemeEditorPage";
import ContractManagementPage from "@/pages/ContractManagementPage";
import LibraryManagementPage from "@/pages/LibraryManagementPage";
import KinderwerkPage from "@/pages/KinderwerkPage";
import UsecasesPage from "@/pages/UsecasesPage";
import FlowStudioPage from "@/pages/FlowStudioPage";
import SitesOverviewPage from "@/modules/sites/pages/SitesOverviewPage";
import SiteBuilderPage from "@/modules/sites/pages/SiteBuilderPage";
import CentralPlanningPage from "@/pages/CentralPlanningPage";
import WriterStudioPage from "@/pages/WriterStudioPage";
import ConnectgroepenPage from "@/pages/ConnectgroepenPage";
import ConnectLessenPage from "@/pages/ConnectLessenPage";
import PresentationPage from "@/pages/PresentationPage";
import DemoPage from "@/pages/DemoPage";
import ShopProviders from "@/pages/shop/ShopProviders";
import ShopLegacyRedirect from "@/pages/shop/ShopLegacyRedirect";
import ShopHomePage from "@/pages/shop/ShopHomePage";
import ShopProductsPage from "@/pages/shop/ShopProductsPage";
import ShopProductDetailPage from "@/pages/shop/ShopProductDetailPage";
import ShopCheckoutPage from "@/pages/shop/ShopCheckoutPage";
import ShopAccountPage from "@/pages/shop/ShopAccountPage";
import LibraryPublicProviders from "@/pages/library/LibraryPublicProviders";
import LibraryPublicHomePage from "@/pages/library/LibraryPublicHomePage";
import LibraryPublicBookPage from "@/pages/library/LibraryPublicBookPage";
import LibraryPublicReturnPage from "@/pages/library/LibraryPublicReturnPage";
import LeiderschapOverviewPage from "@/modules/leiderschap/pages/OverviewPage";
import LeiderschapMeetingsPage from "@/modules/leiderschap/pages/MeetingsPage";
import LeiderschapMeetingDetailPage from "@/modules/leiderschap/pages/MeetingDetailPage";
import LeiderschapDecisionsPage from "@/modules/leiderschap/pages/DecisionsPage";
import LeiderschapActionItemsPage from "@/modules/leiderschap/pages/ActionItemsPage";
import LeiderschapSettingsPage from "@/modules/leiderschap/pages/SettingsPage";
import SprekersEngagementsPage from "@/modules/sprekers/pages/EngagementsPage";
import SprekersEngagementDetailPage from "@/modules/sprekers/pages/EngagementDetailPage";
import SprekersProfilesPage from "@/modules/sprekers/pages/ProfilesPage";
import SprekersTemplatesPage from "@/modules/sprekers/pages/TemplatesPage";
import SprekersCanvasTemplatesPage from "@/modules/sprekers/pages/CanvasTemplatesPage";
import SprekersCanvasEditorPage from "@/modules/sprekers/pages/CanvasEditorPage";
import SprekersSlideStudioPage from "@/modules/sprekers/pages/SlideStudioPage";
import SprekersLivePresenterPage from "@/modules/sprekers/pages/LivePresenterPage";
import SprekersSessionPairPage from "@/modules/sprekers/pages/SessionPairPage";
import SprekersLiveControlPage from "@/modules/sprekers/pages/LiveControlPage";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { getLandingRoute, getProjectedRestrictedModule, isRestrictedRouteAllowed, NO_ACCESS_ROUTE } from "@/lib/landingRoute";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-teal-200"></div>
          <p className="text-slate-500">Laden...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (location.pathname === '/dashboard' && getLandingRoute(user) !== '/dashboard') {
    return <Navigate to={getLandingRoute(user)} replace />;
  }

  const isInbox = location.pathname === '/inbox' || location.pathname.startsWith('/inbox/');
  const restrictedModule = getProjectedRestrictedModule(user);
  if (
    restrictedModule
    && !isInbox
    && location.pathname !== '/dashboard'
    && !isRestrictedRouteAllowed(restrictedModule, location.pathname)
  ) {
    return <Navigate to={getLandingRoute(user)} replace />;
  }

  return children;
};

const ReservationsRoute = ({ children, manage = false }) => {
  const { user, loading, hasCapability, hasModule } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-50" />;
  if (!user) return <Navigate to="/login" replace />;
  const capability = manage ? 'reservations.manage' : 'reservations.view';
  if (!hasModule('reserveringen') || !hasCapability(capability)) {
    const landing = getLandingRoute(user);
    return <Navigate to={landing === '/bookings' ? NO_ACCESS_ROUTE : landing} replace />;
  }
  return children;
};

const ModuleRoute = ({ children, moduleKey, capability = null, anyCapabilities = [] }) => {
  const { user, loading, hasCapability, hasModule } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-slate-50" />;
  if (!user) return <Navigate to="/login" replace />;

  const capabilityAllowed = capability
    ? hasCapability(capability)
    : (anyCapabilities.length ? anyCapabilities.some((key) => hasCapability(key)) : true);

  if (!hasModule(moduleKey) || !capabilityAllowed) {
    const landing = getLandingRoute(user);
    return <Navigate to={landing === location.pathname ? NO_ACCESS_ROUTE : landing} replace />;
  }
  return children;
};

const WorshipRoute = ({ children }) => (
  <ModuleRoute moduleKey="worship">{children}</ModuleRoute>
);

const SongwritingRoute = ({ children }) => (
  <ModuleRoute moduleKey="worship" capability="songwriting_access">{children}</ModuleRoute>
);

const KinderwerkRoute = ({ children }) => (
  <ModuleRoute moduleKey="kinderwerk" capability="kinderwerk_access">{children}</ModuleRoute>
);

const CentralPlanningRoute = ({ children }) => (
  <ModuleRoute moduleKey="central_planning" capability="central_planning_access">{children}</ModuleRoute>
);

const SprekersRoute = ({ children }) => (
  <ModuleRoute moduleKey="sprekers" capability="speakers_access">{children}</ModuleRoute>
);

const ConnectLessonWriterRoute = ({ children }) => (
  <ModuleRoute moduleKey="connectlessen" capability="connectlessen_manage">{children}</ModuleRoute>
);

const UsecasesRoute = ({ children }) => {
  const { user, loading, hasCapability } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-50" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasCapability('usecases_view')) return <Navigate to="/dashboard" replace />;
  return children;
};

const FlowRoute = ({ children }) => {
  const { user, loading, hasCapability } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-50" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasCapability('flow_view') && !hasCapability('flow_manage')) return <Navigate to="/dashboard" replace />;
  return children;
};

const SitesRoute = ({ children }) => (
  <ModuleRoute moduleKey="sites">{children}</ModuleRoute>
);

const PlatformAdminRoute = ({ children }) => {
  const { user, loading, isPlatformAdmin } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-50" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isPlatformAdmin) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRoutes() {
  const location = useLocation();

  return (
    <PageErrorBoundary resetKey={location.key || `${location.pathname}${location.search}`}>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/book/:orgId" element={<PublicBookingPage />} />
      <Route path="/share/worship/:serviceId" element={<PublicSetlistPage />} />
      <Route path="/share/connectles/:token" element={<PublicConnectlesPage />} />
      <Route path="/presentatie" element={<PresentationPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/shop/:orgId/*" element={<ShopLegacyRedirect />} />
      <Route path="/:orgSlug/shop" element={<ShopProviders />}>
        <Route index element={<ShopHomePage />} />
        <Route path="products" element={<ShopProductsPage />} />
        <Route path="product/:slug" element={<ShopProductDetailPage />} />
        <Route path="checkout" element={<ShopCheckoutPage />} />
        <Route path="account" element={<ShopAccountPage />} />
      </Route>
      <Route path="/:orgSlug/bibliotheek" element={<LibraryPublicProviders />}>
        <Route index element={<LibraryPublicHomePage />} />
        <Route path="boek/:slug" element={<LibraryPublicBookPage />} />
        <Route path="b/:bookId" element={<LibraryPublicBookPage byId />} />
        <Route path="inleveren" element={<LibraryPublicReturnPage />} />
      </Route>

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute><DashboardPage /></ProtectedRoute>
      } />
      <Route path="/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
      <Route path={NO_ACCESS_ROUTE} element={
        <ProtectedRoute>
          <div className="min-h-screen bg-slate-50 p-8"><div className="mx-auto mt-16 max-w-xl rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm"><h1 className="text-xl font-semibold text-slate-900">Geen Zoea-module beschikbaar</h1><p className="mt-2 text-sm text-slate-500">Je hebt momenteel geen toegang tot een Zoea-module. Neem contact op met een beheerder.</p></div></div>
        </ProtectedRoute>
      } />
      <Route path="/resources" element={
        <ReservationsRoute manage><ResourcesPage /></ReservationsRoute>
      } />
      <Route path="/bookings" element={
        <ReservationsRoute><BookingsPage /></ReservationsRoute>
      } />
      <Route path="/bookings/overview" element={
        <ReservationsRoute><BookingsOverviewPage /></ReservationsRoute>
      } />
      <Route path="/bookings/public" element={
        <ReservationsRoute manage><PublicBookingLinkPage /></ReservationsRoute>
      } />
      <Route path="/relations" element={
        <ProtectedRoute><RelationsPage /></ProtectedRoute>
      } />
      <Route path="/customers" element={<Navigate to="/relations" replace />} />
      <Route path="/members" element={
        <ProtectedRoute><MembersPage /></ProtectedRoute>
      } />
      <Route path="/team" element={
        <ProtectedRoute><TeamPage /></ProtectedRoute>
      } />
      <Route path="/keys" element={
        <ModuleRoute moduleKey="sleutelbeheer" capability="keys_access"><KeyManagementPage /></ModuleRoute>
      } />
      <Route path="/inventory" element={
        <ProtectedRoute><InventoryPage /></ProtectedRoute>
      } />
      <Route path="/buildings" element={
        <ProtectedRoute><BuildingManagementPage /></ProtectedRoute>
      } />
      <Route path="/commerce" element={
        <ProtectedRoute><CommercePage /></ProtectedRoute>
      } />
      <Route path="/commerce/webshop/vormgeving" element={
        <ProtectedRoute><ThemeEditorPage /></ProtectedRoute>
      } />
      <Route path="/contracts" element={
        <ModuleRoute moduleKey="contractbeheer" capability="contracts_access"><ContractManagementPage /></ModuleRoute>
      } />
      <Route path="/library" element={
        <ModuleRoute moduleKey="bibliotheek" capability="library_access"><LibraryManagementPage /></ModuleRoute>
      } />
      <Route path="/kinderwerk" element={
        <KinderwerkRoute><KinderwerkPage /></KinderwerkRoute>
      } />
      <Route path="/usecases" element={
        <UsecasesRoute><UsecasesPage /></UsecasesRoute>
      } />
      <Route path="/sites" element={<SitesRoute><SitesOverviewPage /></SitesRoute>} />
      <Route path="/sites/:websiteId" element={<SitesRoute><SiteBuilderPage /></SitesRoute>} />
      <Route path="/flows" element={<FlowRoute><FlowStudioPage /></FlowRoute>} />
      <Route path="/flows/new" element={<FlowRoute><FlowStudioPage createMode /></FlowRoute>} />
      <Route path="/flows/:flowId" element={<FlowRoute><FlowStudioPage /></FlowRoute>} />
      <Route path="/planning" element={
        <CentralPlanningRoute><CentralPlanningPage /></CentralPlanningRoute>
      } />
      <Route path="/writer" element={
        <ModuleRoute moduleKey="writer_studio" capability="writer_access"><WriterStudioPage /></ModuleRoute>
      } />
      <Route path="/leiderschap" element={
        <ModuleRoute moduleKey="leiderschap" capability="leiderschap_access"><LeiderschapOverviewPage /></ModuleRoute>
      } />
      <Route path="/leiderschap/vergaderingen" element={
        <ModuleRoute moduleKey="leiderschap" capability="leiderschap_access"><LeiderschapMeetingsPage /></ModuleRoute>
      } />
      <Route path="/leiderschap/vergaderingen/:meetingId" element={
        <ModuleRoute moduleKey="leiderschap" capability="leiderschap_access"><LeiderschapMeetingDetailPage /></ModuleRoute>
      } />
      <Route path="/leiderschap/besluiten" element={
        <ModuleRoute moduleKey="leiderschap" capability="leiderschap_access"><LeiderschapDecisionsPage /></ModuleRoute>
      } />
      <Route path="/leiderschap/actiepunten" element={
        <ModuleRoute moduleKey="leiderschap" capability="leiderschap_access"><LeiderschapActionItemsPage /></ModuleRoute>
      } />
      <Route path="/leiderschap/instellingen" element={
        <ModuleRoute moduleKey="leiderschap" capability="leiderschap_access"><LeiderschapSettingsPage /></ModuleRoute>
      } />
      <Route path="/sprekers" element={
        <SprekersRoute><SprekersEngagementsPage /></SprekersRoute>
      } />
      <Route path="/sprekers/profielen" element={
        <SprekersRoute><SprekersProfilesPage /></SprekersRoute>
      } />
      <Route path="/sprekers/sjablonen" element={
        <SprekersRoute><SprekersTemplatesPage /></SprekersRoute>
      } />
      <Route path="/sprekers/canvas-sjablonen" element={
        <SprekersRoute><SprekersCanvasTemplatesPage /></SprekersRoute>
      } />
      <Route path="/sprekers/:engagementId" element={
        <SprekersRoute><SprekersEngagementDetailPage /></SprekersRoute>
      } />
      <Route path="/sprekers/:engagementId/studio" element={
        <SprekersRoute><SprekersSlideStudioPage /></SprekersRoute>
      } />
      <Route path="/sprekers/:engagementId/canvas-editor" element={
        <SprekersRoute><SprekersCanvasEditorPage /></SprekersRoute>
      } />
      <Route path="/sprekers/sessies/:sessionId/presenteren" element={
        <SprekersRoute><SprekersLivePresenterPage /></SprekersRoute>
      } />
      <Route path="/sprekers/sessies/:sessionId/koppelen" element={
        <SprekersRoute><SprekersSessionPairPage /></SprekersRoute>
      } />
      <Route path="/sprekers/sessies/:sessionId/bedienen" element={
        <SprekersRoute><SprekersLiveControlPage /></SprekersRoute>
      } />
      <Route path="/connectgroepen" element={
        <ProtectedRoute><ConnectgroepenPage /></ProtectedRoute>
      } />
      <Route path="/connectlessen" element={
        <ConnectLessonWriterRoute><ConnectLessenPage /></ConnectLessonWriterRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute><SettingsPage /></ProtectedRoute>
      } />
      <Route path="/superadmin" element={
        <PlatformAdminRoute><SuperAdminPage /></PlatformAdminRoute>
      } />
      <Route path="/heating" element={
        <ModuleRoute moduleKey="heating" capability="heating_admin"><HeatingPage /></ModuleRoute>
      } />
      <Route path="/worship" element={<WorshipRoute><WorshipPage section="dashboard" /></WorshipRoute>} />
      <Route path="/worship/planning" element={<WorshipRoute><WorshipPage section="planning" /></WorshipRoute>} />
      <Route path="/worship/roster" element={<WorshipRoute><WorshipPage section="roster" /></WorshipRoute>} />
      <Route path="/worship/my-planning" element={<WorshipRoute><WorshipPage section="my-planning" /></WorshipRoute>} />
      <Route path="/worship/setlists" element={<WorshipRoute><WorshipPage section="setlists" /></WorshipRoute>} />
      <Route path="/worship/setlists/library" element={<WorshipRoute><WorshipSetlistLibraryPage /></WorshipRoute>} />
      <Route path="/worship/songs" element={<WorshipRoute><WorshipPage section="songs" /></WorshipRoute>} />
      <Route path="/worship/songwriting" element={<SongwritingRoute><SongwritingStudioPage /></SongwritingRoute>} />
      <Route path="/worship/songwriting/:projectId" element={<SongwritingRoute><SongwritingStudioPage /></SongwritingRoute>} />
      <Route path="/worship/team" element={<WorshipRoute><WorshipPage section="team" /></WorshipRoute>} />
      <Route path="/worship/patterns" element={<WorshipRoute><WorshipPage section="patterns" /></WorshipRoute>} />
      <Route path="/worship/rehearsals" element={<WorshipRoute><WorshipPage section="rehearsals" /></WorshipRoute>} />
      <Route path="/worship/files" element={<WorshipRoute><WorshipPage section="files" /></WorshipRoute>} />
      <Route path="/worship/settings" element={<WorshipRoute><WorshipPage section="settings" /></WorshipRoute>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </PageErrorBoundary>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <PwaInstallPrompt />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
